// src/utils/insuranceDossierPdf.js
// Insurance Dossier (§5 assureur / §17 Phase 2) — cédule d'assurance d'une collection.
// Inventaire + valeur à assurer + état/localisation (risque) + police existante (table insurance).
// ARTKIV fournit la donnée ; l'assureur analyse valeur et risque. Ne vaut pas police (§13).
// jsPDF v3 : import jsPDF from "jspdf" + autoTable(doc, {...}).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BRAND_UPPER } from "../lib/rebrand";

const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString("fr-FR")} €` : "—");
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const currentValue = (a) =>
  Number(a?.current_estimated_value ?? a?.valuation_estimated_value ?? a?.estimated_value ?? a?.value ?? 0) || 0;
const CONDITION = { excellent: "Excellent", very_good: "Très bon", good: "Bon", fair: "Moyen", very_poor: "Très moyen", poor: "Mauvais", heavily_damaged: "Très abîmé", partially_restorable: "Part. restaurable", irreparable: "Irréparable" };

// insByArtwork : { [artwork_id]: { insurer, insurance_type, value, expiry_date, idnumber_insurance } }
export function generateInsuranceDossier({ collectionName = "", ownerName = "", artworks = [], insByArtwork = {} } = {}) {
  const doc = new jsPDF("l", "mm", "a4"); // paysage : cédule large
  const M = 14;
  let y = 16;

  const total = artworks.length;
  const totalToInsure = artworks.reduce((s, a) => s + currentValue(a), 0);
  const insured = artworks.filter((a) => insByArtwork[a.id]);
  const insuredValue = insured.reduce((s, a) => s + (Number(insByArtwork[a.id]?.value) || currentValue(a)), 0);

  // ---- En-tête ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text("DOSSIER D'ASSURANCE — CÉDULE", M, y);
  doc.setFontSize(17); doc.setTextColor(20);
  y += 8; doc.text(doc.splitTextToSize(collectionName || "Collection", 260), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  y += 6; if (ownerName) { doc.text(`Titulaire : ${ownerName}`, M, y); y += 4; }

  // ---- Synthèse ----
  autoTable(doc, {
    startY: y + 2, theme: "plain", margin: { left: M, right: M },
    styles: { fontSize: 10, cellPadding: 1.5 },
    columnStyles: { 0: { fontStyle: "bold", textColor: 120, cellWidth: 70 } },
    body: [
      ["Nombre d'œuvres", String(total)],
      ["Valeur totale à assurer", eur(totalToInsure)],
      ["Œuvres assurées", `${insured.length} / ${total}`],
      ["Valeur déjà assurée", eur(insuredValue)],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 7;

  // ---- Cédule ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text("Cédule des œuvres", M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [["Œuvre", "Artiste", "Valeur à assurer", "État", "Localisation", "Assureur", "N° police", "Échéance"]],
    body: artworks
      .slice()
      .sort((a, b) => currentValue(b) - currentValue(a))
      .map((a) => {
        const ins = insByArtwork[a.id];
        return [
          a.title || "(sans titre)",
          a.artist || "—",
          eur(currentValue(a)),
          CONDITION[a.condition] || a.condition || "—",
          a.storage_location_detail || "—",
          ins?.insurer || "— (non assurée)",
          ins?.idnumber_insurance || "—",
          ins ? fmt(ins.expiry_date) : "—",
        ];
      }),
    headStyles: { fillColor: [26, 26, 26], fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 1.4 },
    columnStyles: { 2: { halign: "right" } },
    // Ligne surlignée si non assurée
    didParseCell: (data) => {
      if (data.section === "body" && data.row.raw[5] && String(data.row.raw[5]).includes("non assurée")) {
        data.cell.styles.textColor = [180, 68, 58];
      }
    },
  });
  y = (doc.lastAutoTable?.finalY || y) + 5;

  // ---- Pied assureur (§5/§13) ----
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  const foot =
    "Cédule établie via " + BRAND_UPPER + " le " + new Date().toLocaleDateString("fr-FR") + ". " +
    "Document informatif destiné à l'analyse assurantielle : il ne constitue pas une police d'assurance. " +
    "" + BRAND_UPPER + " structure la donnée (valeur, documentation, risque) ; l'assureur apprécie la valeur et le risque.";
  doc.text(doc.splitTextToSize(foot, 265), M, 200);

  const slug = (collectionName || "collection").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  doc.save(`dossier-assurance-${slug || "collection"}.pdf`);
}
